import apiInstance from '@/api';
import toastMessage from '@/components/Toast';
import { ThunkApiConfig } from '@/models/ThunkApiConfig';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { RootState } from '../store';

import {
  CreateClusterAndRelationshipPayload,
  CreateNewClassificationTagPayload,
  CreateNewReportsForClusterPayload,
  GetReportsForClusterDataMinimalPayload,
} from './actions';
import {
  GetNewClusterDataPayload,
  GetReportsForClusterDataPayload,
  GetReportsForClusterDataSuccessPayload,
  GetSavedReportsForClusterDataSuccessPayload,
  OnDemandReportAcknowledgement,
  OnDemandReportPayload,
  createClusterAndRelationship,
  createDashboardClassClusterReport,
  createNewClassificationTag,
  createNewReportsForCluster,
  deleteClusterFrequencyReport,
  generateOnDemandReport,
  getClassTagsTableData,
  getClassificationTagsData,
  getClusters,
  getClustersTableData,
  getNewClusterData,
  getSavedReportsForClusterData,
  updateClassTagData,
  updateClustersTableData,
  updateRegularClusterReport,
  updateSponsorDashReport,
} from './actions';
import { Cluster, Frequency, ReportsForClusterData, Reporttype } from './types';

export const getClustersThunk = createAsyncThunk.withTypes<ThunkApiConfig>()(
  'reportCenter/getClusters',
  async (payload: any, { dispatch, rejectWithValue }) => {
    dispatch(getClusters.request({}));
    try {
      const response = await apiInstance.reportCenter.getClusters(payload);
      dispatch(getClusters.success({ data: response.data.data }));
    } catch (error: any) {
      dispatch(getClusters.failure());
      toastMessage.error('Failed to load clusters: ' + error.message);
      return rejectWithValue(error.message || 'Error fetching clusters.');
    }
  },
);

export const getClassificationTagsDataThunk =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'reportCenter/getClassificationTagsData',
    async (payload: any, { dispatch, rejectWithValue }) => {
      dispatch(getClassificationTagsData.request({}));
      try {
        const response =
          await apiInstance.reportCenter.getClassificationTagsData(payload);
        dispatch(
          getClassificationTagsData.success({ data: response.data.data }),
        );
      } catch (error: any) {
        dispatch(getClassificationTagsData.failure());
        toastMessage.error(
          'Failed to load classification tags: ' + error.message,
        );
        return rejectWithValue(
          error.message || 'Error fetching classification tags.',
        );
      }
    },
  );

export const createNewClassificationTagThunk =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'reportCenter/createNewClassificationTag',
    async (
      payload: CreateNewClassificationTagPayload,
      { dispatch, rejectWithValue },
    ) => {
      dispatch(createNewClassificationTag.request(payload));
      try {
        const response =
          await apiInstance.reportCenter.createNewClassificationTag(payload);
        dispatch(createNewClassificationTag.success(response.data.data));
        toastMessage.success('Classification tag created successfully!');
      } catch (error: any) {
        dispatch(createNewClassificationTag.failure());
        toastMessage.error(
          'Failed to create classification tag: ' + error.message,
        );
        return rejectWithValue(
          error.message || 'Error creating classification tag.',
        );
      }
    },
  );

export const getNewClusterDataThunk =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'reportCenter/getNewClusterData',
    async (
      payload: GetNewClusterDataPayload,
      { dispatch, rejectWithValue },
    ) => {
      dispatch(getNewClusterData.request({}));
      try {
        const response =
          await apiInstance.reportCenter.getNewClusterData(payload);
        const data = response.data?.result?.data?.data; // unwrap
        dispatch(getNewClusterData.success({ data }));
        return data;
      } catch (error: any) {
        dispatch(getNewClusterData.failure());
        toastMessage.error('Failed to load new cluster data: ' + error.message);
        return rejectWithValue(
          error.message || 'Error fetching new cluster data.',
        );
      }
    },
  );

export const createClusterAndRelationshipThunk =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'reportCenter/createClusterAndRelationship',
    async (
      payload: CreateClusterAndRelationshipPayload,
      { dispatch, rejectWithValue },
    ) => {
      dispatch(createClusterAndRelationship.request());
      try {
        const response =
          await apiInstance.reportCenter.createClusterAndRelationship(payload);
        dispatch(
          createClusterAndRelationship.success({ data: response.data.data }),
        );
        toastMessage.success('Cluster created successfully!');
        return response.data.data;
      } catch (error: any) {
        dispatch(
          createClusterAndRelationship.failure({ message: error.message }),
        );
        toastMessage.error('Failed to create cluster: ' + error.message);
        return rejectWithValue(error.message || 'Error creating cluster.');
      }
    },
  );

export const getSavedReportsForClusterDataThunk =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'reportCenter/fetchSavedReportsForClusterData',
    async (
      payload: GetReportsForClusterDataPayload,
      { dispatch, rejectWithValue },
    ) => {
      dispatch(getSavedReportsForClusterData.request());

      try {
        const response =
          await apiInstance.reportCenter.getSavedReportsForClusterData(
            payload,
            {
              headers: {
                Authorization: 'Bearer token',
                'Content-Type': 'application/json',
              },
            },
          );

        // <-- Correct extraction
        const raw = response.data?.data?.data;

        const mapped: GetSavedReportsForClusterDataSuccessPayload = {
          clusters: raw?.clusters ?? [],
          frequencies: raw?.frequencies ?? [],
          emails: raw?.emails ?? [],
          reports: raw?.reports ?? [],
          monte_carlo_report: raw?.monte_carlo_report ?? null,
        };

        dispatch(getSavedReportsForClusterData.success(mapped));
        return mapped;
      } catch (error: any) {
        const errMsg =
          error.message ||
          'An error occurred while fetching Saved Reports For Cluster Data.';
        dispatch(getSavedReportsForClusterData.failure());
        toastMessage.error(errMsg);
        return rejectWithValue(errMsg);
      }
    },
  );

export const createNewReportsForClusterThunk = createAsyncThunk<
  void,
  CreateNewReportsForClusterPayload,
  { rejectValue: string }
>(
  'reportCenter/createNewReportsForCluster',
  async (payload, { dispatch, rejectWithValue }) => {
    dispatch(createNewReportsForCluster.request(payload));

    try {
      const response =
        await apiInstance.reportCenter.createNewReportsForCluster(payload);

      const apiData = response?.data?.data; // ✅ correct path

      // check API success
      if (response?.data?.success && apiData?.success) {
        const clusterId = apiData.data?.cluster_id;
        const message = apiData.message || 'Reports created successfully!';
        dispatch(
          createNewReportsForCluster.success({ clusterId: clusterId || 0 }),
        );

        toastMessage.success(message); // ✅ success toast
        return;
      } else {
        const message =
          apiData?.message || 'Failed to create reports for cluster.';
        dispatch(createNewReportsForCluster.failure({ error: message }));
        toastMessage.error(message); // ❌ only on failure
        return rejectWithValue(message);
      }
    } catch (error: any) {
      const message = error?.message || 'Error creating cluster reports.';
      dispatch(createNewReportsForCluster.failure({ error: message }));
      toastMessage.error('Failed to create reports for cluster: ' + message);
      return rejectWithValue(message);
    }
  },
);

export const createDashboardClassClusterReportThunk =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'reportCenter/createDashboardClassClusterReport',
    async (payload: any, { dispatch, rejectWithValue }) => {
      dispatch(createDashboardClassClusterReport.request({}));
      try {
        const response =
          await apiInstance.reportCenter.createDashboardClassClusterReport(
            payload,
          );
        dispatch(
          createDashboardClassClusterReport.success({
            data: response.data.data,
          }),
        );
      } catch (error: any) {
        dispatch(createDashboardClassClusterReport.failure());
        toastMessage.error(
          'Failed to create dashboard cluster report: ' + error.message,
        );
        return rejectWithValue(
          error.message || 'Error creating dashboard cluster report.',
        );
      }
    },
  );

export const updateRegularClusterReportThunk =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'reportCenter/updateRegularClusterReport',
    async (payload: any, { dispatch, rejectWithValue }) => {
      dispatch(updateRegularClusterReport.request({}));
      try {
        const response =
          await apiInstance.reportCenter.updateRegularClusterReport(payload);
        dispatch(getClustersTableDataThunk());
        dispatch(
          updateRegularClusterReport.success({ data: response.data.data }),
        );
      } catch (error: any) {
        dispatch(updateRegularClusterReport.failure());
        toastMessage.error(
          'Failed to update regular cluster report: ' + error.message,
        );
        return rejectWithValue(
          error.message || 'Error updating cluster report.',
        );
      }
    },
  );

export const updateSponsorDashReportThunk =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'reportCenter/updateSponsorDashReport',
    async (payload: any, { dispatch, rejectWithValue }) => {
      dispatch(updateSponsorDashReport.request({}));
      try {
        const response =
          await apiInstance.reportCenter.updateSponsorDashReport(payload);
        dispatch(updateSponsorDashReport.success({ data: response.data.data }));
      } catch (error: any) {
        dispatch(updateSponsorDashReport.failure());
        toastMessage.error(
          'Failed to update sponsor dashboard report: ' + error.message,
        );
        return rejectWithValue(
          error.message || 'Error updating sponsor dashboard report.',
        );
      }
    },
  );

export const deleteClusterFrequencyReportThunk =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'reportCenter/deleteClusterFrequencyReport',
    async (payload: any, { dispatch, rejectWithValue }) => {
      dispatch(deleteClusterFrequencyReport.request({}));
      try {
        const response =
          await apiInstance.reportCenter.deleteClusterFrequencyReport(payload);
        dispatch(getClustersTableDataThunk());
        dispatch(
          deleteClusterFrequencyReport.success({ data: response.data.data }),
        );
      } catch (error: any) {
        dispatch(deleteClusterFrequencyReport.failure());
        toastMessage.error(
          'Failed to delete cluster frequency report: ' + error.message,
        );
        return rejectWithValue(
          error.message || 'Error deleting cluster frequency report.',
        );
      }
    },
  );

export const getClassTagsTableDataThunk =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'reportCenter/getClassTagsTableData',
    async (undefined, { dispatch, rejectWithValue }) => {
      dispatch(getClassTagsTableData.request());
      try {
        const response = await apiInstance.reportCenter.getClassTagsTableData();
        dispatch(getClassTagsTableData.success({ data: response.data.data }));
      } catch (error: any) {
        dispatch(getClassTagsTableData.failure());
        toastMessage.error(
          'Failed to load class tags table data: ' + error.message,
        );
        return rejectWithValue(
          error.message || 'Error fetching class tags table data.',
        );
      }
    },
  );

export const updateClassTagDataThunk =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'reportCenter/updateClassTagData',
    async (payload: any, { dispatch, rejectWithValue }) => {
      dispatch(updateClassTagData.request({}));
      try {
        const response =
          await apiInstance.reportCenter.updateClassTagData(payload);
        dispatch(getClassTagsTableDataThunk());
        dispatch(updateClassTagData.success({ data: response.data.data }));
      } catch (error: any) {
        dispatch(updateClassTagData.failure());
        toastMessage.error('Failed to update class tag: ' + error.message);
        return rejectWithValue(error.message || 'Error updating class tag.');
      }
    },
  );

export const getClustersTableDataThunk =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'reportCenter/getClustersTableData',
    async (undefined, { dispatch, rejectWithValue }) => {
      dispatch(getClustersTableData.request());
      try {
        const response = await apiInstance.reportCenter.getClustersTableData();
        dispatch(getClustersTableData.success({ data: response.data.data }));
      } catch (error: any) {
        dispatch(getClustersTableData.failure());
        toastMessage.error(
          'Failed to load clusters table data: ' + error.message,
        );
        return rejectWithValue(
          error.message || 'Error fetching clusters table data.',
        );
      }
    },
  );

export const updateClustersTableDataThunk =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'reportCenter/updateClustersTableData',
    async (payload: any, { dispatch, rejectWithValue }) => {
      dispatch(updateClustersTableData.request({}));
      try {
        const response =
          await apiInstance.reportCenter.updateClustersTableData(payload);
        dispatch(updateClustersTableData.success({ data: response.data.data }));
      } catch (error: any) {
        dispatch(updateClustersTableData.failure());
        toastMessage.error(
          'Failed to update clusters table data: ' + error.message,
        );
        return rejectWithValue(
          error.message || 'Error updating clusters table data.',
        );
      }
    },
  );

export const generateOnDemandReportThunk =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'reportCenter/generateOnDemandReport',
    async (payload: OnDemandReportPayload, { dispatch, rejectWithValue }) => {
      dispatch(generateOnDemandReport.request(payload));
      try {
        const response =
          await apiInstance.reportCenter.generateOnDemandReport(payload);
        dispatch(generateOnDemandReport.success(response.data));
        return response.data as OnDemandReportAcknowledgement;
      } catch (error: any) {
        dispatch(generateOnDemandReport.failure());
        toastMessage.error(
          'Failed to generate on-demand report: ' + error.message,
        );
        return rejectWithValue(
          error.message || 'Error generating on-demand report.',
        );
      }
    },
  );
